import type { CSSProperties, ReactNode } from "react";
import { light as blueprintTheme } from "@resideo/blueprint-theme";
import styles from "./EditControlsRow.module.css";

// Pull color + weight from the shared Blueprint theme so this row picks up
// the platform-wide muted-text tokens instead of hardcoding hex/values.
// Font-size and line-height are left in CSS because there is no matching
// small-body typography token exposed on `blueprintTheme.typography`.
const descriptionStyle: CSSProperties = {
  color: blueprintTheme.colors.faded,
  fontWeight: blueprintTheme.typography.body.fontWeight,
};

export interface EditControlsRowProps {
  /** Field label shown on the left. */
  label: string;
  /** Supporting description shown under the label. */
  description?: string;
  /**
   * When provided, wires the label to the associated control (via `htmlFor`)
   * so clicking the label focuses the control. Omit for rows whose control
   * is a button rather than a native form input.
   */
  controlId?: string;
  /** The right-hand form control (Select, Button, custom, etc.). */
  children: ReactNode;
}

/**
 * Presentational two-column row used by the Edit Controls form.
 * Left column: label + description. Right column: the control.
 */
export const EditControlsRow = ({
  label,
  description,
  controlId,
  children,
}: EditControlsRowProps) => (
  <div className={styles.row}>
    <div className={styles.labelColumn}>
      {controlId ? (
        <label htmlFor={controlId} className={styles.label}>
          {label}
        </label>
      ) : (
        <span className={styles.label}>{label}</span>
      )}
      {description && (
        <p className={styles.description} style={descriptionStyle}>
          {description}
        </p>
      )}
    </div>
    <div className={styles.controlColumn}>{children}</div>
  </div>
);

export default EditControlsRow;
